/**
 * useDrawerResize — drag-to-resize + persisted width for a right-anchored
 * slide-out / drawer, extracted from ProDetailSlideOver so any drawer can
 * adopt the same feel without re-implementing the mouse math.
 *
 * What it gives you (mirrors the pro detail drawer):
 *   • a `width` (px) clamped to [minWidth, viewport − nav], read synchronously
 *     from localStorage on mount so there's no flash from default → saved;
 *   • an `onHandleMouseDown` to wire to a drag handle on the drawer's inner
 *     (left) edge — dragging leftward grows the panel;
 *   • snap-to-nav: when the left edge nears the app nav's right edge it snaps
 *     flush, and a snapped drawer follows the nav as it resizes;
 *   • `onHandleDoubleClick` to toggle snap-to-nav (optional to wire up);
 *   • persistence to localStorage under the caller's key (+ a paired snap key).
 *
 * Persistence is localStorage-only here (the simplest version of the pro
 * behavior). ProDetailSlideOver additionally mirrors the width to the
 * per-user userSettings service for cross-device sync; that extra hop is
 * intentionally left to the consumer if it wants it.
 */
import { useEffect, useRef, useState } from 'react';

export interface UseDrawerResizeOptions {
  /** Whether the drawer is open — gates the nav-resize sync listeners. */
  isOpen: boolean;
  /** localStorage key the chosen width is persisted under. */
  storageKey: string;
  /** Smallest allowed width in px (default 400, matching the pro drawer). */
  minWidth?: number;
  /**
   * Default width when nothing is stored yet. Either a fixed px number or a
   * fraction (0–1] of the viewport width. Defaults to 0.7 (the pro drawer's
   * first-open ratio). Always re-clamped to [minWidth, max].
   */
  defaultWidth?: number;
}

export interface UseDrawerResizeResult {
  /** Current panel width in px, already clamped. Feed this to the panel. */
  width: number;
  /** Whether the drawer is currently snapped flush to the app nav. */
  snappedToNav: boolean;
  /** mousedown handler for the drag handle. */
  onHandleMouseDown: (e: React.MouseEvent) => void;
  /** dblclick handler for the drag handle (toggles snap-to-nav). */
  onHandleDoubleClick: () => void;
}

const FALLBACK_NAV_RIGHT = 80; // used when no [data-app-nav] is found
const SNAP_THRESHOLD = 24; // px — how close before snapping

/** Right edge (x) of the app nav, in viewport coords. Falls back to a fixed
 *  gutter when no nav is mounted (e.g. login screens). */
const getNavRight = (): number => {
  const el = document.querySelector<HTMLElement>('[data-app-nav]');
  if (!el) return FALLBACK_NAV_RIGHT;
  return Math.max(0, el.getBoundingClientRect().right);
};

const maxWidthForNav = (navRight: number, minWidth: number) =>
  Math.max(minWidth, window.innerWidth - navRight);

const computeDefault = (minWidth: number, defaultWidth: number) => {
  const max = maxWidthForNav(getNavRight(), minWidth);
  // <= 1 → treat as a viewport fraction; otherwise a fixed px width.
  const desired = defaultWidth <= 1 ? window.innerWidth * defaultWidth : defaultWidth;
  return Math.max(minWidth, Math.min(max, desired));
};

const clampWidth = (n: number, minWidth: number) =>
  Math.max(minWidth, Math.min(maxWidthForNav(getNavRight(), minWidth), n));

const readLocalWidth = (storageKey: string): number | null => {
  try {
    const v = localStorage.getItem(storageKey);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const snapKeyFor = (storageKey: string) => `${storageKey}__snapped`;

const readSnapped = (storageKey: string): boolean => {
  try {
    return localStorage.getItem(snapKeyFor(storageKey)) === 'true';
  } catch {
    return false;
  }
};

export function useDrawerResize({
  isOpen,
  storageKey,
  minWidth = 400,
  defaultWidth = 0.7,
}: UseDrawerResizeOptions): UseDrawerResizeResult {
  const [snappedToNav, setSnappedToNav] = useState<boolean>(() => readSnapped(storageKey));
  const snappedRef = useRef(snappedToNav);
  snappedRef.current = snappedToNav;

  // Initial width is read synchronously from localStorage to avoid a flash
  // from default → saved on every open.
  const [width, setWidth] = useState<number>(() =>
    clampWidth(readLocalWidth(storageKey) ?? computeDefault(minWidth, defaultWidth), minWidth),
  );
  const widthRef = useRef(width);
  widthRef.current = width;

  // While snapped, keep the drawer flush with the nav as the nav resizes
  // (collapse/expand toggle, window resize). We deliberately do NOT re-snap on
  // initial open — the saved width is the source of truth at open time.
  useEffect(() => {
    if (!isOpen) return;
    const nav = document.querySelector<HTMLElement>('[data-app-nav]');
    const sync = () => {
      if (!snappedRef.current) return;
      setWidth(maxWidthForNav(getNavRight(), minWidth));
    };
    let ro: ResizeObserver | null = null;
    if (nav && typeof ResizeObserver !== 'undefined') {
      let firstObserve = true; // ResizeObserver always fires once on attach
      ro = new ResizeObserver(() => {
        if (firstObserve) {
          firstObserve = false;
          return;
        }
        sync();
      });
      ro.observe(nav);
    }
    window.addEventListener('resize', sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [isOpen, snappedToNav, minWidth]);

  const persist = (w: number, snapped: boolean) => {
    try {
      localStorage.setItem(storageKey, String(w));
      localStorage.setItem(snapKeyFor(storageKey), String(snapped));
    } catch {
      /* ignore quota errors */
    }
  };

  const onHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    // The width the drag has most recently asked for. `widthRef` only catches
    // up on the next render, so when the final mousemove and the mouseup land
    // in the same tick (fast release, automation) it still holds the previous
    // render's width — persisting from it would save a stale value and compute
    // the snapped flag from it. Track the latest value in the closure instead.
    let latest = startWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      // Drawer is anchored to the right; dragging the handle leftward
      // (clientX decreases) grows the panel.
      const raw = startWidth + (startX - ev.clientX);
      const maxW = maxWidthForNav(getNavRight(), minWidth);
      // Snap when the drawer's left edge gets within SNAP_THRESHOLD of the
      // nav's right edge.
      const snap = raw >= maxW - SNAP_THRESHOLD;
      latest = snap ? maxW : Math.max(minWidth, Math.min(maxW, raw));
      setWidth(latest);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // If we settled at the nav's right edge, mark snapped so future nav
      // resizes drag the drawer along with them.
      const maxW = maxWidthForNav(getNavRight(), minWidth);
      const isSnapped = latest >= maxW - 1;
      setSnappedToNav(isSnapped);
      persist(latest, isSnapped);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Double-click the handle to toggle snap-to-nav. When already snapped, a
  // second double-click restores the previous (or default) width.
  const onHandleDoubleClick = () => {
    if (snappedRef.current) {
      const restored = clampWidth(
        readLocalWidth(storageKey) ?? computeDefault(minWidth, defaultWidth),
        minWidth,
      );
      const navMax = maxWidthForNav(getNavRight(), minWidth);
      const next = restored >= navMax - 1 ? Math.max(minWidth, navMax * 0.6) : restored;
      setSnappedToNav(false);
      setWidth(next);
      persist(next, false);
      return;
    }
    const navMax = maxWidthForNav(getNavRight(), minWidth);
    setSnappedToNav(true);
    setWidth(navMax);
    persist(navMax, true);
  };

  return { width, snappedToNav, onHandleMouseDown, onHandleDoubleClick };
}

export default useDrawerResize;
